import { MAX_BYLINE_CONNECTION_ICONS } from '../utils/getBylineConnectionIcons.ts';

import type { preloadOutboundBylineSsrStateQuery$data } from './__generated__/preloadOutboundBylineSsrStateQuery.graphql.ts';

export type OutboundBylineSsrStatus = 'present' | 'empty' | 'unavailable';

// `iconCount` (distinct-domain icon estimate for the placeholder) exists only on the 'present'
// variant. CCINT-8915
export type OutboundBylineSsrState = Readonly<
	| { contentId: string; status: 'present'; iconCount: number }
	| { contentId: string; status: 'empty' | 'unavailable' }
>;

export function createOutboundBylineSsrState(
	contentId: string,
	status: 'empty' | 'unavailable',
): OutboundBylineSsrState;
export function createOutboundBylineSsrState(
	contentId: string,
	status: 'present',
	iconCount: number,
): OutboundBylineSsrState;
export function createOutboundBylineSsrState(
	contentId: string,
	status: OutboundBylineSsrStatus,
	iconCount?: number,
): OutboundBylineSsrState {
	if (status === 'present') {
		return Object.freeze({ contentId, status, iconCount: iconCount ?? 0 });
	}
	return Object.freeze({ contentId, status });
}

// Shared by the loading placeholder and the loaded byline, so both reserve alike.
export const getReservedBylineIconCount = (
	outboundState: OutboundBylineSsrState | undefined,
	hasDevDocsConnection: boolean,
): number => {
	const hasOutboundSignal =
		outboundState?.status === 'present' ||
		outboundState?.status === 'unavailable' ||
		hasDevDocsConnection;
	// Without an outbound signal the placeholder is text-only, so reserve no tiles.
	if (!hasOutboundSignal) {
		return 0;
	}
	// 'unavailable' gives no reliable estimate, so reserve the maximum.
	if (outboundState?.status === 'unavailable') {
		return MAX_BYLINE_CONNECTION_ICONS;
	}
	const outboundIconCount = outboundState?.status === 'present' ? outboundState.iconCount : 0;
	const devDocsIconCount = hasDevDocsConnection ? 1 : 0;
	return Math.min(Math.max(outboundIconCount + devDocsIconCount, 1), MAX_BYLINE_CONNECTION_ICONS);
};

const isNullOrUndefined = (value: unknown): value is null | undefined => {
	if (value === null) {
		return true;
	}
	return value === undefined;
};

const getHostname = (url: string): string | undefined => {
	try {
		return new URL(url).hostname;
	} catch {
		return undefined;
	}
};

const countDistinctDomains = (
	externalNodes: ReadonlyArray<{ readonly url: string | null } | null>,
	internalNodes: ReadonlyArray<unknown>,
): number => {
	const domains = new Set<string>();
	for (const node of externalNodes) {
		const url = node?.url;
		if (typeof url !== 'string') {
			continue;
		}
		// Skip host-less schemes (mailto:, tel:) whose empty hostname would count as a domain.
		const hostname = getHostname(url);
		if (hostname) {
			domains.add(hostname);
		}
	}
	if (internalNodes.length > 0) {
		domains.add('__internal__');
	}
	return Math.min(domains.size, MAX_BYLINE_CONNECTION_ICONS);
};

type OutgoingLinksCollections<TExternalNodes, TInternalNodes> = Readonly<{
	externalNodes: TExternalNodes | null | undefined;
	internalNodes: TInternalNodes | null | undefined;
}>;

type CompleteOutgoingLinksCollections<TExternalNodes, TInternalNodes> = Readonly<{
	externalNodes: TExternalNodes;
	internalNodes: TInternalNodes;
}>;

const isValidOutgoingLinksResponse = <TExternalNodes, TInternalNodes>(
	actualContentId: string | null | undefined,
	expectedContentId: string,
	collections: OutgoingLinksCollections<TExternalNodes, TInternalNodes>,
): collections is CompleteOutgoingLinksCollections<TExternalNodes, TInternalNodes> => {
	if (actualContentId !== expectedContentId) {
		return false;
	}
	if (isNullOrUndefined(collections.externalNodes)) {
		return false;
	}
	if (isNullOrUndefined(collections.internalNodes)) {
		return false;
	}
	return true;
};

export const normalizeOutboundBylineSsrState = (
	contentId: string,
	data: preloadOutboundBylineSsrStateQuery$data | null | undefined,
): OutboundBylineSsrState => {
	const contentNodes = data?.content?.nodes;
	if (isNullOrUndefined(contentNodes) || contentNodes.length !== 1) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	const content = contentNodes[0];
	const collections = {
		externalNodes: content?.outgoingLinks?.externalOutgoingLinks?.nodes,
		internalNodes: content?.outgoingLinks?.internalOutgoingLinks?.nodes,
	};
	if (!isValidOutgoingLinksResponse(content?.id, contentId, collections)) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}
	const { externalNodes, internalNodes } = collections;

	const hasMalformedExternalNode = externalNodes.some(
		(node) => typeof node?.url !== 'string' || node.url.length === 0,
	);
	const hasMalformedInternalNode = internalNodes.some((node) => {
		const base = node?.links?.base;
		const webui = node?.links?.webui;
		return !(
			(typeof base === 'string' && base.length > 0) ||
			(typeof webui === 'string' && webui.length > 0)
		);
	});
	if (hasMalformedExternalNode || hasMalformedInternalNode) {
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	if (externalNodes.length === 0 && internalNodes.length === 0) {
		return createOutboundBylineSsrState(contentId, 'empty');
	}

	return createOutboundBylineSsrState(
		contentId,
		'present',
		countDistinctDomains(externalNodes, internalNodes),
	);
};
